import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Store, ArrowLeft } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AFRICAN_COUNTRIES } from "@/lib/countries";
import BottomNav from "@/components/BottomNav";

export default function Onboarding() {
    const { user, setUser, loading } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        shop_name: "",
        whatsapp_number: "",
        call_number: "",
        city: "",
        country: "",
        location: "",
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!loading && !user) navigate("/login");
        if (user) {
            setForm((f) => ({
                ...f,
                shop_name: user.shop_name || "",
                whatsapp_number: user.whatsapp_number || "",
                call_number: user.call_number || "",
                city: user.city || "",
                country: user.country || "",
                location: user.location || "",
            }));
        }
    }, [user, loading, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.shop_name || !form.whatsapp_number || !form.call_number || !form.city || !form.country) {
            toast.error("Merci de remplir tous les champs obligatoires");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/seller/onboard", form);
            setUser(data);
            toast.success("Bienvenue vendeur ! +10 crédits offerts");
            navigate("/upload");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] text-white pb-32 grain-bg">
            <div className="max-w-lg mx-auto px-4 pt-6">
                <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/70 hover:text-white mb-6" data-testid="back-btn">
                    <ArrowLeft className="w-5 h-5" /> <span className="font-body text-sm">Retour</span>
                </button>

                <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 rounded-2xl bg-[#FF5722] flex items-center justify-center">
                        <Store className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="font-display font-black text-3xl tracking-tight">Devenez vendeur</h1>
                        <p className="text-white/60 font-body text-sm">Votre boutique en vidéo, en 2 minutes</p>
                    </div>
                </div>

                <div className="glass rounded-2xl p-4 mb-6">
                    <p className="text-sm font-body text-white/80">
                        <span className="text-[#FFC107] font-semibold">🎁 Bonus :</span> 10 crédits gratuits offerts à l'inscription. Chaque commande WhatsApp reçue coûte 1 crédit.
                    </p>
                </div>

                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <Label htmlFor="shop_name" className="text-white/80 font-body">Nom de la boutique *</Label>
                        <Input
                            id="shop_name"
                            data-testid="input-shop-name"
                            value={form.shop_name}
                            onChange={(e) => setForm({ ...form, shop_name: e.target.value })}
                            placeholder="Ex: Chez Awa - Mode"
                            className="mt-1 bg-white/5 border-white/10 h-12 text-white placeholder:text-white/30"
                        />
                    </div>
                    <div>
                        <Label htmlFor="whatsapp_number" className="text-white/80 font-body">Numéro WhatsApp * (avec code pays)</Label>
                        <Input
                            id="whatsapp_number"
                            data-testid="input-whatsapp"
                            value={form.whatsapp_number}
                            onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                            placeholder="Ex: +225 07 00 00 00 00"
                            className="mt-1 bg-white/5 border-white/10 h-12 text-white placeholder:text-white/30"
                        />
                    </div>
                    <div>
                        <Label htmlFor="call_number" className="text-white/80 font-body">Numéro d'appel *</Label>
                        <Input
                            id="call_number"
                            data-testid="input-call"
                            value={form.call_number}
                            onChange={(e) => setForm({ ...form, call_number: e.target.value })}
                            placeholder="Ex: +225 07 00 00 00 00"
                            className="mt-1 bg-white/5 border-white/10 h-12 text-white placeholder:text-white/30"
                        />
                    </div>
                    <div>
                        <Label htmlFor="city" className="text-white/80 font-body">Ville *</Label>
                        <Input
                            id="city"
                            data-testid="input-city"
                            value={form.city}
                            onChange={(e) => setForm({ ...form, city: e.target.value })}
                            placeholder="Ex: Abidjan, Dakar, Douala..."
                            className="mt-1 bg-white/5 border-white/10 h-12 text-white placeholder:text-white/30"
                        />
                    </div>
                    <div>
                        <Label className="text-white/80 font-body">Pays *</Label>
                        <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                            <SelectTrigger className="mt-1 bg-white/5 border-white/10 h-12 text-white" data-testid="select-country">
                                <SelectValue placeholder="Choisir un pays" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                {AFRICAN_COUNTRIES.map((c) => (
                                    <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="location" className="text-white/80 font-body">Quartier / Adresse (optionnel)</Label>
                        <Input
                            id="location"
                            data-testid="input-location"
                            value={form.location}
                            onChange={(e) => setForm({ ...form, location: e.target.value })}
                            placeholder="Ex: Cocody, Marché Adjamé..."
                            className="mt-1 bg-white/5 border-white/10 h-12 text-white placeholder:text-white/30"
                        />
                    </div>

                    <Button
                        type="submit"
                        data-testid="submit-onboarding"
                        disabled={submitting}
                        className="w-full h-14 orange-btn text-white font-body font-bold text-base rounded-full"
                    >
                        {submitting ? "Enregistrement..." : "Enregistrer ma boutique"}
                    </Button>
                </form>
            </div>
            <BottomNav />
        </div>
    );
}
